import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import {
  X, Copy, Check, Mail, MessageSquare, Loader2, Users as UsersIcon,
  Share2, Upload, ClipboardList, Send, FileText,
} from "lucide-react";

/**
 * ShareSheet v2 — invite people without depending on Chrome-Android.
 *
 * Layout:
 *   1. HERO: native OS share sheet button (primary CTA on mobile,
 *      hidden on desktop browsers that don't support navigator.share)
 *   2. Copy link
 *   3. Quick-paste list — paste names+phones/emails, get per-person
 *      Send SMS / Email / WhatsApp buttons
 *   4. vCard (.vcf) upload — import iPhone/Android contacts file
 *   5. Direct send: SMS to one number, Email to one address
 *   6. Social channels: WhatsApp, X, Facebook, Messenger, LinkedIn,
 *      TikTok (copy), Instagram (copy)
 *
 * Props:
 *   groupId      — group-scoped invite link
 *   eventId      — event-scoped invite link (alternative to groupId)
 *   inviterName  — current user name
 *   onClose      — fn
 */
export default function ShareSheet({ groupId, eventId, inviterName, onClose }) {
  const [link, setLink] = useState("");
  const [contextName, setContextName] = useState("");
  const [contextKind, setContextKind] = useState(""); // "group" or "event"
  const [schedulingMode, setSchedulingMode] = useState(false);
  const [copied, setCopied] = useState(false);

  // Quick-paste
  const [pasted, setPasted] = useState("");
  const parsedContacts = useMemo(() => parseContactsBlob(pasted), [pasted]);

  // vCard
  const [vcardContacts, setVcardContacts] = useState([]);
  const fileInputRef = useRef(null);

  // Single SMS / email
  const [phone, setPhone] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [emailName, setEmailName] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (groupId) {
          const [{ data: l }, { data: g }] = await Promise.all([
            api.get(`/groups/${groupId}/invite-link`),
            api.get(`/groups/${groupId}`),
          ]);
          setLink(l.link);
          setContextName(g.name || "the group");
          setContextKind("group");
          setSchedulingMode(!!g.scheduling_mode);
        } else if (eventId) {
          const [{ data: l }, { data: e }] = await Promise.all([
            api.get(`/events/${eventId}/invite-link`),
            api.get(`/events/${eventId}`),
          ]);
          setLink(l.link);
          setContextName(e.title || "the event");
          setContextKind("event");
        }
      } catch (err) {
        console.error("ShareSheet: failed to fetch invite link", err);
      }
    })();
  }, [groupId, eventId]);

  // Brand spelling shifts in scheduling mode: "Bornday" (no space) reads as
  // a sharper, planning-app name — vs the warm "Born Day" used elsewhere.
  const brand = schedulingMode ? "Bornday" : "Born Day";

  // Minimum-character share text. Per Steve (Feb 2026): include only
  // WHO is asking, WHAT it's for, and the link. No "Hi!", no taglines,
  // no app-pitch sentences. Same string is reused across SMS, email,
  // WhatsApp, X, native share — so trimming here trims everywhere.
  const inviter = inviterName || "A friend";
  const ctxName = contextName || (contextKind === "event" ? "an event" : "a group");
  const shareText = `${inviter} invited you to ${ctxName}: ${link}`;
  const shortText = shareText;
  const nativeSupported = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const copy = async (text, label = "link") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Clipboard API can be blocked by permissions/insecure contexts.
      console.warn("Clipboard write failed:", err);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({
        title: ctxName,
        text: shareText,
        url: link,
      });
    } catch (err) {
      // AbortError = user cancelled the share sheet; expected.
      if (err?.name !== "AbortError") console.warn("Native share failed:", err);
    }
  };

  const smsHref = (n) => {
    const num = (n || "").replace(/[^\d+]/g, "");
    const body = encodeURIComponent(shareText);
    return num ? `sms:${num}?body=${body}` : `sms:?body=${body}`;
  };
  const emailHref = (addr) => {
    // Minimum subject + body. The body IS the share text (link included).
    const subject = encodeURIComponent(`Invite: ${ctxName}`);
    const body = encodeURIComponent(shareText);
    return `mailto:${addr}?subject=${subject}&body=${body}`;
  };
  const whatsappHref = (n) => {
    const num = (n || "").replace(/[^\d+]/g, "");
    return `https://wa.me/${num.replace(/^\+/, "")}?text=${encodeURIComponent(shareText)}`;
  };

  // ---------------- Quick-paste handler ----------------
  const sendAllPasted = (channel) => {
    parsedContacts.forEach((c) => {
      let href = null;
      if (channel === "sms" && c.phone) href = smsHref(c.phone);
      if (channel === "email" && c.email) href = emailHref(c.email, c.name);
      if (channel === "whatsapp" && c.phone) href = whatsappHref(c.phone);
      if (href) window.open(href, "_blank");
    });
  };

  // ---------------- vCard upload ----------------
  const onVcardFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const contacts = parseVCard(text);
    setVcardContacts(contacts);
  };

  // ---------------- Email send (existing backend mailto endpoint) ----------------
  const sendEmail = async (e) => {
    e.preventDefault();
    if (!groupId) {
      // event-only share: fall back to direct mailto
      window.location.href = emailHref(emailAddr, emailName);
      return;
    }
    setEmailBusy(true);
    try {
      const { data } = await api.post("/groups/invite-email", {
        group_id: groupId, recipient_email: emailAddr, recipient_name: emailName || null,
      });
      window.location.href = data.mailto;
    } catch {
      window.location.href = emailHref(emailAddr, emailName);
    } finally {
      setEmailBusy(false);
    }
  };

  // ---------------- Social platforms ----------------
  const platforms = [
    {
      id: "whatsapp", label: "WhatsApp", color: "#25D366",
      action: () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank"),
    },
    {
      id: "facebook", label: "Facebook", color: "#1877F2",
      action: () => window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(shortText)}`,
        "_blank", "noopener,noreferrer,width=600,height=600"),
    },
    {
      id: "messenger", label: "Messenger", color: "#0084FF",
      action: () => {
        const deep = `fb-messenger://share?link=${encodeURIComponent(link)}`;
        const win = window.open(deep, "_blank");
        setTimeout(() => {
          if (!win || win.closed) {
            window.open(
              `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
              "_blank", "noopener,noreferrer,width=600,height=600");
          }
        }, 400);
      },
    },
    {
      id: "x", label: "X (Twitter)", color: "#000000",
      action: () => window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shortText)}`,
        "_blank", "noopener,noreferrer,width=600,height=500"),
    },
    {
      id: "linkedin", label: "LinkedIn", color: "#0A66C2",
      action: () => window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
        "_blank", "noopener,noreferrer,width=600,height=600"),
    },
    {
      id: "tiktok", label: "TikTok", color: "#000000", copyOnly: true,
      action: async () => {
        await copy(shortText, "tiktok");
        window.open("https://www.tiktok.com/", "_blank", "noopener,noreferrer");
      },
    },
    {
      id: "instagram", label: "Instagram", color: "#E4405F", copyOnly: true,
      action: async () => {
        await copy(shortText, "instagram");
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      },
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="share-sheet"
    >
      <div
        className="bd-surface p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Share2 size={18} />
            <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.03em" }}>Send invite</h2>
          </div>
          <button onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.4rem" }} data-testid="share-sheet-close">
            <X size={14} />
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: "var(--text-dim)" }}>
          Anyone with the link joins <strong style={{ color: "var(--text)" }}>{contextName}</strong>.
        </p>

        {/* HERO: Native OS share sheet — primary path on iPhone/Android */}
        {nativeSupported && (
          <button
            onClick={nativeShare}
            className="bd-btn w-full mb-4"
            style={{
              padding: "1rem 1.25rem",
              fontSize: "1.05rem",
              fontWeight: 700,
              boxShadow: "0 6px 22px rgba(255, 122, 89, 0.35)",
            }}
            data-testid="share-native-hero"
          >
            <Share2 size={18} /> Share
          </button>
        )}

        {/* Copy link */}
        <div className="grid grid-cols-[1fr_auto] gap-2 mb-5">
          <input className="bd-input bd-mono" readOnly value={link} data-testid="share-link-input" />
          <button
            className="bd-btn"
            onClick={() => copy(link, "link")}
            data-testid="share-copy-link"
          >
            {copied === "link" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
        </div>

        {/* QUICK PASTE LIST */}
        <details className="mb-5" open={parsedContacts.length > 0} data-testid="share-paste">
          <summary className="cursor-pointer font-semibold flex items-center gap-2 mb-2">
            <ClipboardList size={16} /> Paste a list of contacts
            <span className="bd-chip" style={{ fontSize: "0.65rem" }}>iPhone &amp; desktop friendly</span>
          </summary>
          <p className="text-xs mb-2" style={{ color: "var(--text-dim)" }}>
            One per line, comma-separated: name, phone or email.
          </p>
          <textarea
            className="bd-input"
            rows={4}
            placeholder={`Mom, +1 555 123 4567\nDad, dad@example.com\nGrandma, +15555550199`}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            style={{ fontFamily: "inherit" }}
            data-testid="share-paste-textarea"
          />
          {parsedContacts.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{parsedContacts.length} parsed</span>
                <div className="flex gap-2">
                  <button className="bd-btn bd-btn-ghost" style={{ padding: "0.35rem 0.65rem", fontSize: "0.75rem" }}
                    onClick={() => sendAllPasted("sms")} data-testid="share-paste-all-sms">
                    <MessageSquare size={12} /> SMS all
                  </button>
                  <button className="bd-btn bd-btn-ghost" style={{ padding: "0.35rem 0.65rem", fontSize: "0.75rem" }}
                    onClick={() => sendAllPasted("email")} data-testid="share-paste-all-email">
                    <Mail size={12} /> Email all
                  </button>
                </div>
              </div>
              <ContactList contacts={parsedContacts} smsHref={smsHref} emailHref={emailHref} whatsappHref={whatsappHref} prefix="paste" />
            </div>
          )}
        </details>

        {/* VCARD UPLOAD */}
        <details className="mb-5" open={vcardContacts.length > 0} data-testid="share-vcard">
          <summary className="cursor-pointer font-semibold flex items-center gap-2 mb-2">
            <Upload size={16} /> Import a contacts file (.vcf)
            <span className="bd-chip" style={{ fontSize: "0.65rem" }}>iPhone export</span>
          </summary>
          <p className="text-xs mb-2" style={{ color: "var(--text-dim)" }}>
            iPhone: Contacts → select → Share → Save to Files. Upload the .vcf — parsed locally.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".vcf,text/vcard"
            onChange={onVcardFile}
            className="bd-input"
            data-testid="share-vcard-input"
          />
          {vcardContacts.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{vcardContacts.length} contacts loaded</span>
                <button className="bd-btn bd-btn-ghost" style={{ padding: "0.35rem 0.65rem", fontSize: "0.75rem" }}
                  onClick={() => setVcardContacts([])} data-testid="share-vcard-clear">
                  Clear
                </button>
              </div>
              <ContactList contacts={vcardContacts} smsHref={smsHref} emailHref={emailHref} whatsappHref={whatsappHref} prefix="vcard" />
            </div>
          )}
        </details>

        <div className="bd-divider mb-5" />

        {/* Direct SMS / Email */}
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm"><MessageSquare size={14} /> Text one person</h3>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <input
                className="bd-input bd-mono"
                type="tel"
                placeholder="+1 555 123 4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="share-sms-phone"
              />
              <a className="bd-btn" href={smsHref(phone)} data-testid="share-sms-send">
                <Send size={12} />
              </a>
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm"><Mail size={14} /> Email one person</h3>
            <form onSubmit={sendEmail} className="grid grid-cols-[1fr_auto] gap-2">
              <input
                className="bd-input"
                type="email"
                placeholder="their@email.com"
                required
                value={emailAddr}
                onChange={(e) => setEmailAddr(e.target.value)}
                data-testid="share-email-addr"
              />
              <button className="bd-btn" type="submit" disabled={emailBusy} data-testid="share-email-send">
                {emailBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              </button>
            </form>
          </div>
        </div>

        {/* Social platforms */}
        <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
          <UsersIcon size={14} /> Send to a platform
        </h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {platforms.map((p) => (
            <button
              key={p.id}
              onClick={p.action}
              className="bd-card flex flex-col items-center justify-center gap-2 py-3 transition hover:scale-[1.02]"
              style={{ padding: "0.75rem 0.5rem", textAlign: "center" }}
              data-testid={`share-${p.id}`}
            >
              <span
                className="w-9 h-9 grid place-items-center rounded-full text-white font-bold"
                style={{ background: p.color }}
              >
                {p.label[0]}
              </span>
              <span className="text-xs font-semibold">{p.label}</span>
              {p.copyOnly && (
                <span className="text-[10px]" style={{ color: "var(--text-dim)" }}>copy + open</span>
              )}
              {copied === p.id && (
                <span className="text-[10px]" style={{ color: "var(--primary)" }}>copied!</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Helpers ---------------- */

function ContactList({ contacts, smsHref, emailHref, whatsappHref, prefix }) {
  return (
    <ul className="space-y-2 max-h-64 overflow-y-auto" data-testid={`${prefix}-contact-list`}>
      {contacts.map((c, i) => (
        <li
          key={`${c.phone || ""}|${c.email || ""}|${c.name || ""}|${i}`}
          className="flex items-center justify-between gap-2 p-2 rounded"
          style={{ border: "1px solid var(--border)" }}
          data-testid={`${prefix}-contact-${i}`}
        >
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{c.name || "(no name)"}</div>
            <div className="text-xs bd-mono" style={{ color: "var(--text-dim)" }}>
              {c.phone || c.email || "—"}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {c.phone && (
              <a className="bd-btn bd-btn-ghost"
                 style={{ padding: "0.3rem 0.55rem", fontSize: "0.7rem" }}
                 href={smsHref(c.phone)} data-testid={`${prefix}-sms-${i}`}>
                SMS
              </a>
            )}
            {c.phone && (
              <a className="bd-btn bd-btn-ghost"
                 style={{ padding: "0.3rem 0.55rem", fontSize: "0.7rem" }}
                 href={whatsappHref(c.phone)} target="_blank" rel="noreferrer"
                 data-testid={`${prefix}-wa-${i}`}>
                WhatsApp
              </a>
            )}
            {c.email && (
              <a className="bd-btn bd-btn-ghost"
                 style={{ padding: "0.3rem 0.55rem", fontSize: "0.7rem" }}
                 href={emailHref(c.email, c.name)} data-testid={`${prefix}-email-${i}`}>
                Email
              </a>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Parse a free-form paste of contacts. Each line = one contact.
 *  Supports: "Name, +1 555-555-5555" / "Name, email@x.com" / "+15555555555"
 *  / "email@x.com" / "Name\t+1...". Phone vs email detected automatically. */
function parseContactsBlob(text) {
  if (!text) return [];
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const out = [];
  const phoneRe = /(\+?\d[\d\s\-().]{6,}\d)/;
  const emailRe = /([A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,})/;
  for (const line of lines) {
    const phone = (line.match(phoneRe) || [])[0]?.replace(/[\s\-().]/g, "") || "";
    const email = (line.match(emailRe) || [])[0] || "";
    // Name = whatever's left before the first delimiter, with phone/email stripped
    let name = line.replace(phoneRe, "").replace(emailRe, "");
    name = name.replace(/[,\t;]+/g, " ").trim();
    if (name || phone || email) {
      out.push({ name, phone, email });
    }
  }
  return out;
}

/** Minimal vCard 3.0/4.0 parser — extracts FN, TEL, EMAIL. */
function parseVCard(text) {
  const cards = text.split(/BEGIN:VCARD/i).slice(1);
  const contacts = [];
  for (const raw of cards) {
    const body = raw.split(/END:VCARD/i)[0];
    const lines = body.split(/\r?\n/);
    let name = "", phone = "", email = "";
    for (const ln of lines) {
      const [keyPart, ...valueParts] = ln.split(":");
      if (!keyPart || valueParts.length === 0) continue;
      const value = valueParts.join(":").trim();
      const key = keyPart.split(";")[0].toUpperCase();
      if (!name && (key === "FN" || key === "N")) name = value.replace(/;/g, " ").trim();
      if (!phone && key === "TEL") phone = value.replace(/[\s\-().]/g, "");
      if (!email && key === "EMAIL") email = value;
    }
    if (name || phone || email) contacts.push({ name, phone, email });
  }
  return contacts;
}
